/**
 * Find the hands in a photo the user brings, in the browser — and read a
 * photo's pixels for the two-click placement.
 *
 * MediaPipe's Hand Landmarker, the same model and version
 * `scripts/onbody/detect.html` ran over the library, loaded only when someone
 * actually picks a photo of their own: the bundle is dynamically imported from
 * jsDelivr (so none of it is in the studio's JS), the WASM comes from the same
 * pinned path, and the 7.8 MB model from Google's model bucket. The production
 * CSP allows exactly those two path prefixes (`server/index.ts`).
 *
 * Detection gives landmarks; the walks across every finger, the widths and the
 * hand order come from `anchorMath.ts` — the code that built
 * `public/onbody/placements.json` — so a user's photo is stored in the same v2
 * shape (every hand, all 21 image + world landmarks) and measured the way the
 * library was.
 */
import {
  measureHand,
  orderHands,
  placeHand,
  samplerFromRgba,
  type Landmark,
  type PixelSampler,
  type PlacedHand,
} from "./anchorMath";

/** The version installed in webgl-jewelry-viewer (package.json ^0.10.14 →
 *  0.10.35), which is what detect.html ran. Pinned: the CSP names this path. */
export const MEDIAPIPE_VERSION = "0.10.35";
export const MEDIAPIPE_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}`;
export const HAND_MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task";

/** The slice of @mediapipe/tasks-vision this file uses. */
interface Category {
  categoryName: string;
  score: number;
}
interface HandLandmarkerResult {
  landmarks: Landmark[][];
  worldLandmarks?: Landmark[][];
  handedness?: Category[][];
  /** Older name of `handedness`. */
  handednesses?: Category[][];
}
interface HandLandmarkerLike {
  detect(image: HTMLCanvasElement): HandLandmarkerResult;
}
interface VisionModule {
  FilesetResolver: { forVisionTasks(base: string): Promise<unknown> };
  HandLandmarker: { createFromOptions(fileset: unknown, options: unknown): Promise<HandLandmarkerLike> };
}

let landmarker: Promise<HandLandmarkerLike> | null = null;

/** One landmarker per page; a failed load is forgotten so the next photo
 *  tries again (a flaky network should not disable detection for the session). */
function loadLandmarker(): Promise<HandLandmarkerLike> {
  if (!landmarker) {
    landmarker = (async () => {
      const url = `${MEDIAPIPE_BASE}/vision_bundle.mjs`;
      const mod = (await import(/* @vite-ignore */ url)) as VisionModule;
      const fileset = await mod.FilesetResolver.forVisionTasks(`${MEDIAPIPE_BASE}/wasm`);
      // The detector settings detect.html used for the library.
      return mod.HandLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: HAND_MODEL_URL },
        runningMode: "IMAGE",
        numHands: 4,
        minHandDetectionConfidence: 0.3,
        minHandPresenceConfidence: 0.3,
        minTrackingConfidence: 0.3,
      });
    })();
    landmarker.catch(() => {
      landmarker = null;
    });
  }
  return landmarker;
}

export interface Detection {
  /** Every hand found, the one to wear the ring on first; empty when none. */
  hands: PlacedHand[];
  /** The photo's pixel size, as decoded (EXIF orientation applied). */
  w: number;
  h: number;
}

/** A canvas holding `image` at most `maxSide` pixels on its long side. */
function drawScaled(image: CanvasImageSource & { width: number; height: number }, maxSide: number) {
  const k = Math.min(1, maxSide / Math.max(image.width, image.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.width * k));
  canvas.height = Math.max(1, Math.round(image.height * k));
  const g = canvas.getContext("2d", { willReadFrequently: true });
  if (!g) throw new Error("This browser cannot read the photo's pixels.");
  g.drawImage(image, 0, 0, canvas.width, canvas.height);
  return { canvas, g };
}

/**
 * Detect every hand in `image`. The photo is measured at most `maxSide` pixels
 * on its long side — the library's photos were up to 2752 px — but everything
 * stored is normalised, so it applies to the full-size backdrop unchanged.
 */
export async function detectHands(image: Blob, maxSide = 2048): Promise<Detection> {
  const bmp = await createImageBitmap(image);
  const w = bmp.width;
  const h = bmp.height;
  const { canvas, g } = drawScaled(bmp, maxSide);
  bmp.close();
  const W = canvas.width, H = canvas.height;
  const sampler = samplerFromRgba(g.getImageData(0, 0, W, H).data, W, H);

  const result = (await loadLandmarker()).detect(canvas);
  const hd = result.handedness ?? result.handednesses ?? [];
  const hands = (result.landmarks ?? []).map((lm, i) =>
    placeHand(
      measureHand(lm, result.worldLandmarks?.[i] ?? [], W, H, sampler, hd[i]?.[0]?.categoryName ?? null, hd[i]?.[0]?.score ?? null),
      W,
      H,
    ),
  );
  return { hands: orderHands(hands, W, H), w, h };
}

/** A photo's pixels for measuring, at most `maxSide` on its long side, and the
 *  factor from photo pixels to sampler pixels. */
export interface PhotoPixels {
  sampler: PixelSampler;
  /** sampler pixels per photo pixel */
  scale: number;
}

/**
 * Read a photo's pixels — a File/Blob always works; a URL only when its host
 * allows it (CORS). Null when the browser will not hand the pixels over (a
 * tainted canvas, or no CORS header at all), which the two-click placement
 * answers with a width slider instead of a measurement.
 */
export async function readPhotoPixels(src: Blob | string, maxSide = 2048): Promise<PhotoPixels | null> {
  try {
    let source: (CanvasImageSource & { width: number; height: number }) | null = null;
    let close = () => {};
    if (typeof src === "string") {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.decoding = "async";
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("no CORS"));
        img.src = src;
      });
      source = Object.assign(img, { width: img.naturalWidth, height: img.naturalHeight });
    } else {
      const bmp = await createImageBitmap(src);
      source = bmp;
      close = () => bmp.close();
    }
    const fullW = source.width;
    const { canvas, g } = drawScaled(source, maxSide);
    close();
    const data = g.getImageData(0, 0, canvas.width, canvas.height).data; // throws on a tainted canvas
    return { sampler: samplerFromRgba(data, canvas.width, canvas.height), scale: canvas.width / fullW };
  } catch {
    return null;
  }
}

/** A photo's natural pixel size, without reading its pixels (no CORS needed). */
export function measureImage(url: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => reject(new Error("Could not read the photo's size."));
    img.src = url;
  });
}
